import { apiClient } from './apiClient';
import { uploadFile } from './uploads.service';
import type { ApiResponse } from '@/types/api.types';

// ─── Sources ─────────────────────────────────────────────────────────────────

export interface KnowledgeBaseSource {
  kbSourceId: number;
  accountId: number;
  sourceType: string;
  name: string;
  kbFolderPath?: string | null;
  status: 'active' | 'paused' | 'disabled' | string;
  config: Record<string, unknown>;
  webhookUrl?: string;
  lastSyncedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface KnowledgeBaseSourceWithSecret extends KnowledgeBaseSource {
  webhookUrl: string;
  webhookSecret: string;
}

export interface CreateKnowledgeBaseSourceRequest {
  name: string;
  sourceType?: string;
  status?: 'active' | 'paused' | 'disabled';
  config?: Record<string, unknown>;
}

export interface UpdateKnowledgeBaseSourceRequest {
  name?: string;
  kbFolderPath?: string | null;
  status?: 'active' | 'paused' | 'disabled';
  config?: Record<string, unknown>;
}

export function listKnowledgeBaseSources(): Promise<KnowledgeBaseSource[]> {
  return apiClient
    .get<ApiResponse<KnowledgeBaseSource[]>>('/kb/sources')
    .then((response) => response.data.data);
}

export function createKnowledgeBaseSource(
  request: CreateKnowledgeBaseSourceRequest,
): Promise<KnowledgeBaseSourceWithSecret> {
  return apiClient
    .post<ApiResponse<KnowledgeBaseSourceWithSecret>>('/kb/sources', request)
    .then((response) => response.data.data);
}

export function updateKnowledgeBaseSource(
  sourceId: number,
  request: UpdateKnowledgeBaseSourceRequest,
): Promise<KnowledgeBaseSource> {
  return apiClient
    .put<ApiResponse<KnowledgeBaseSource>>(`/kb/sources/${sourceId}`, request)
    .then((response) => response.data.data);
}

export function deleteKnowledgeBaseSource(sourceId: number, force = false): Promise<void> {
  return apiClient
    .delete(`/kb/sources/${sourceId}${force ? '?force=1' : ''}`)
    .then(() => undefined);
}

export function regenerateKnowledgeBaseSourceSecret(
  sourceId: number,
): Promise<KnowledgeBaseSourceWithSecret> {
  return apiClient
    .post<ApiResponse<KnowledgeBaseSourceWithSecret>>(`/kb/sources/${sourceId}/regenerate-secret`)
    .then((response) => response.data.data);
}

// ─── Documents ───────────────────────────────────────────────────────────────

export interface KnowledgeBaseDocument {
  kbDocumentId: number;
  accountId: number;
  kbSourceId: number;
  sourceType: string;
  sourceName: string;
  externalId: string;
  title: string;
  url: string;
  category: string;
  tags: string[];
  status: string;
  version: number;
  bodyFormat: 'markdown' | 'html' | string;
  editable: boolean;
  sourceUpdatedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface KbDocumentAttachment {
  kbDocumentAttachmentId: number;
  kbDocumentId: number | null;
  /**
   * Absolute S3 URL. NOT reachable from a browser — the `kb/` prefix is private,
   * so an anonymous GET returns an AccessDenied XML document. Use
   * `openKbAttachment()` to get a signed URL instead.
   */
  url: string;
  /** API path that mints a short-lived signed URL for this object. */
  downloadUrl: string;
  originalFilename: string;
  mimeType: string | null;
  sizeBytes: number;
  kind: 'image' | 'document' | string;
  isInline: boolean;
  createdAt: string | null;
}

export interface KnowledgeBaseDocumentDetail extends Omit<KnowledgeBaseDocument, 'sourceType' | 'sourceName'> {
  body: string;
  source: {
    kbSourceId: number;
    sourceType: string;
    name: string;
    status: string;
  } | null;
  chunks: Array<{
    kbDocumentChunkId: number;
    chunkIndex: number;
    content: string;
  }>;
  attachments: KbDocumentAttachment[];
}

export interface KnowledgeBaseDocumentListParams {
  q?: string;
  category?: string;
  tag?: string;
  sourceId?: number;
  sourceType?: string;
  status?: string;
  sort?: 'updated_desc' | 'updated_asc' | 'title_asc' | 'title_desc';
  page?: number;
  perPage?: number;
}

export interface KnowledgeBaseDocumentListResponse {
  data: KnowledgeBaseDocument[];
  total: number;
  page: number;
  perPage: number;
}

export function listKnowledgeBaseDocuments(
  params: KnowledgeBaseDocumentListParams = {},
): Promise<KnowledgeBaseDocumentListResponse> {
  return apiClient
    .get<ApiResponse<KnowledgeBaseDocument[]>>('/kb/documents', { params })
    .then((response) => ({
      data: response.data.data,
      total: Number(response.data.meta?.total ?? response.data.data.length),
      page: Number(response.data.meta?.page ?? 1),
      perPage: Number(response.data.meta?.perPage ?? response.data.data.length),
    }));
}

export function getKnowledgeBaseDocument(documentId: number): Promise<KnowledgeBaseDocumentDetail> {
  return apiClient
    .get<ApiResponse<KnowledgeBaseDocumentDetail>>(`/kb/documents/${documentId}`)
    .then((response) => response.data.data);
}

export interface SaveKnowledgeBaseDocumentRequest {
  title: string;
  body?: string;
  bodyFormat?: 'markdown' | 'html';
  url?: string;
  category?: string;
  tags?: string[];
  status?: 'active' | 'archived' | 'draft';
  externalId?: string;
  /**
   * IDs of orphaned `kb_document_attachments` rows (uploaded via /kb/uploads
   * with no kbDocumentId) that should be linked to the document on save.
   */
  attachmentIds?: number[];
}

export function createKnowledgeBaseDocument(
  request: SaveKnowledgeBaseDocumentRequest,
): Promise<KnowledgeBaseDocumentDetail> {
  return apiClient
    .post<ApiResponse<KnowledgeBaseDocumentDetail>>('/kb/documents', request)
    .then((response) => response.data.data);
}

export function updateKnowledgeBaseDocument(
  documentId: number,
  request: Partial<SaveKnowledgeBaseDocumentRequest>,
): Promise<KnowledgeBaseDocumentDetail> {
  return apiClient
    .put<ApiResponse<KnowledgeBaseDocumentDetail>>(`/kb/documents/${documentId}`, request)
    .then((response) => response.data.data);
}

export function deleteKnowledgeBaseDocument(documentId: number): Promise<void> {
  return apiClient.delete(`/kb/documents/${documentId}`).then(() => undefined);
}

export interface KnowledgeBaseFacet {
  name: string;
  count: number;
}

export function listKnowledgeBaseCategories(): Promise<KnowledgeBaseFacet[]> {
  return apiClient
    .get<ApiResponse<KnowledgeBaseFacet[]>>('/kb/categories')
    .then((response) => response.data.data);
}

export function listKnowledgeBaseTags(): Promise<KnowledgeBaseFacet[]> {
  return apiClient
    .get<ApiResponse<KnowledgeBaseFacet[]>>('/kb/tags')
    .then((response) => response.data.data);
}

// ─── Attachments (S3-backed) ─────────────────────────────────────────────────

/**
 * Upload a file to the KB attachments bucket. Pass `kbDocumentId` to attach
 * directly to an existing doc; omit it for the "create new doc" flow and pass
 * the returned `kbDocumentAttachmentId` in `SaveKnowledgeBaseDocumentRequest.attachmentIds`
 * so the backend links it during save. `isInline=true` flags the file as an
 * editor-embedded image (hidden from the attachments list).
 */
export function uploadKbAttachment(
  file: File,
  opts: { kbDocumentId?: number; isInline?: boolean } = {},
): Promise<KbDocumentAttachment> {
  const extra: Record<string, string> = {};
  if (opts.kbDocumentId) extra.kbDocumentId = String(opts.kbDocumentId);
  if (opts.isInline) extra.isInline = '1';
  // uploadFile transparently switches files > 4 MB to the presigned-S3 flow.
  return uploadFile('/kb/uploads', file, extra).then((response) => response.data.data);
}

/**
 * Resolve an attachment to a short-lived signed S3 URL.
 *
 * The stored `url` points into a private bucket prefix, so it can never be used
 * as an href — the browser gets an S3 AccessDenied XML page. This asks the API
 * to mint a presigned URL, and to say so when the object is missing from
 * storage altogether.
 */
export function getKbAttachmentUrl(attachmentId: number): Promise<string> {
  return apiClient
    .get<ApiResponse<{ url: string }>>(`/kb/uploads/${attachmentId}/download`)
    .then((response) => response.data.data.url);
}

/**
 * Resolve an attachment and open it in a new tab.
 *
 * Opens via a synthesized anchor click rather than `window.open`, which popup
 * blockers reject when it runs after an await — the same pattern the lab
 * download helpers use for their presigned `{redirect}` responses.
 */
export async function openKbAttachment(attachmentId: number): Promise<void> {
  const url = await getKbAttachmentUrl(attachmentId);
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function deleteKbAttachment(attachmentId: number): Promise<void> {
  return apiClient.delete(`/kb/uploads/${attachmentId}`).then(() => undefined);
}

export function listKbDocumentAttachments(documentId: number): Promise<KbDocumentAttachment[]> {
  return apiClient
    .get<ApiResponse<KbDocumentAttachment[]>>(`/kb/documents/${documentId}/attachments`)
    .then((response) => response.data.data);
}

// ─── Sync events / audit ─────────────────────────────────────────────────────

export interface KbSyncEvent {
  kbSyncEventId: number;
  kbSourceId: number;
  sourceName: string;
  sourceType: string;
  externalId: string;
  action: string;
  status: string;
  message: string;
  hasPayload: boolean;
  createdAt: string | null;
}

export interface KbSyncEventDetail extends KbSyncEvent {
  payload: Record<string, unknown> | null;
}

export interface KbSyncEventListParams {
  sourceId?: number;
  status?: string;
  action?: string;
  from?: string;
  to?: string;
  page?: number;
  perPage?: number;
}

export interface KbSyncEventListResponse {
  data: KbSyncEvent[];
  total: number;
  page: number;
  perPage: number;
}

export function listKbSyncEvents(params: KbSyncEventListParams = {}): Promise<KbSyncEventListResponse> {
  return apiClient
    .get<ApiResponse<KbSyncEvent[]>>('/kb/sync-events', { params })
    .then((response) => ({
      data: response.data.data,
      total: Number(response.data.meta?.total ?? response.data.data.length),
      page: Number(response.data.meta?.page ?? 1),
      perPage: Number(response.data.meta?.perPage ?? response.data.data.length),
    }));
}

export function getKbSyncEvent(eventId: number): Promise<KbSyncEventDetail> {
  return apiClient
    .get<ApiResponse<KbSyncEventDetail>>(`/kb/sync-events/${eventId}`)
    .then((response) => response.data.data);
}

export function retryKbSyncEvent(eventId: number): Promise<{ kbDocumentId: number }> {
  return apiClient
    .post<ApiResponse<{ kbDocumentId: number; externalId: string; status: string }>>(
      `/kb/sync-events/${eventId}/retry`,
    )
    .then((response) => response.data.data);
}

// ─── Chapters ────────────────────────────────────────────────────────────────

export interface KbChapter {
  kbChapterId: number;
  accountId: number;
  title: string;
  description: string;
  coverUrl: string;
  category: string;
  tags: string[];
  status: string;
  itemCount: number;
  createdBy: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface KbChapterItem {
  kbChapterItemId: number;
  kbChapterId: number;
  kbDocumentId: number;
  documentTitle: string;
  documentCategory: string;
  documentStatus: string;
  position: number;
  required: boolean;
}

export interface KbChapterDetail extends KbChapter {
  items: KbChapterItem[];
}

export interface SaveKbChapterRequest {
  title: string;
  description?: string;
  coverUrl?: string;
  category?: string;
  tags?: string[];
  status?: 'active' | 'archived' | 'draft';
}

export function listKbChapters(params: { q?: string; status?: string } = {}): Promise<KbChapter[]> {
  return apiClient
    .get<ApiResponse<KbChapter[]>>('/kb/chapters', { params })
    .then((response) => response.data.data);
}

export function getKbChapter(chapterId: number): Promise<KbChapterDetail> {
  return apiClient
    .get<ApiResponse<KbChapterDetail>>(`/kb/chapters/${chapterId}`)
    .then((response) => response.data.data);
}

export function createKbChapter(request: SaveKbChapterRequest): Promise<KbChapter> {
  return apiClient
    .post<ApiResponse<KbChapter>>('/kb/chapters', request)
    .then((response) => response.data.data);
}

export function updateKbChapter(chapterId: number, request: Partial<SaveKbChapterRequest>): Promise<KbChapter> {
  return apiClient
    .put<ApiResponse<KbChapter>>(`/kb/chapters/${chapterId}`, request)
    .then((response) => response.data.data);
}

export function deleteKbChapter(chapterId: number): Promise<void> {
  return apiClient.delete(`/kb/chapters/${chapterId}`).then(() => undefined);
}

export function addKbChapterItem(
  chapterId: number,
  payload: { kbDocumentId: number; required?: boolean },
): Promise<KbChapterItem> {
  return apiClient
    .post<ApiResponse<KbChapterItem>>(`/kb/chapters/${chapterId}/items`, payload)
    .then((response) => response.data.data);
}

export function removeKbChapterItem(chapterId: number, itemId: number): Promise<void> {
  return apiClient.delete(`/kb/chapters/${chapterId}/items/${itemId}`).then(() => undefined);
}

export function reorderKbChapter(chapterId: number, itemIds: number[]): Promise<void> {
  return apiClient.post(`/kb/chapters/${chapterId}/reorder`, { itemIds }).then(() => undefined);
}

// ─── Assignments ─────────────────────────────────────────────────────────────

export interface KbAssignment {
  kbAssignmentId: number;
  accountId: number;
  targetType: 'document' | 'chapter' | string;
  targetId: number;
  targetTitle: string;
  assignedUserId: number;
  assignedByUserId: number | null;
  dueAt: string | null;
  status: 'pending' | 'acknowledged' | 'overdue' | 'canceled' | string;
  acknowledgedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface KbAssignmentListParams {
  userId?: number;
  status?: string;
  targetType?: string;
  targetId?: number;
}

export function listKbAssignments(params: KbAssignmentListParams = {}): Promise<KbAssignment[]> {
  return apiClient
    .get<ApiResponse<KbAssignment[]>>('/kb/assignments', { params })
    .then((response) => response.data.data);
}

export function createKbAssignment(payload: {
  targetType: 'document' | 'chapter';
  targetId: number;
  userIds: number[];
  dueAt?: string | null;
}): Promise<KbAssignment[]> {
  return apiClient
    .post<ApiResponse<KbAssignment[]>>('/kb/assignments', payload)
    .then((response) => response.data.data);
}

export function cancelKbAssignment(assignmentId: number): Promise<void> {
  return apiClient.delete(`/kb/assignments/${assignmentId}`).then(() => undefined);
}

export function listMyKbAssignments(include?: 'all'): Promise<KbAssignment[]> {
  return apiClient
    .get<ApiResponse<KbAssignment[]>>('/kb/my-assignments', {
      params: include ? { include } : undefined,
    })
    .then((response) => response.data.data);
}

export function acknowledgeMyKbAssignment(assignmentId: number): Promise<KbAssignment> {
  return apiClient
    .post<ApiResponse<KbAssignment>>(`/kb/my-assignments/${assignmentId}/acknowledge`)
    .then((response) => response.data.data);
}

// ─── Quizzes ─────────────────────────────────────────────────────────────────

export interface KbQuiz {
  kbQuizId: number;
  accountId: number;
  targetType: 'document' | 'chapter' | string;
  targetId: number;
  targetTitle: string;
  title: string;
  passThreshold: number;
  status: string;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface KbQuizChoice {
  kbQuizChoiceId: number;
  label: string;
  position: number;
  isCorrect: boolean | null;
}

export interface KbQuizQuestion {
  kbQuizQuestionId: number;
  prompt: string;
  position: number;
  questionType: 'single' | 'multi' | string;
  choices: KbQuizChoice[];
}

export interface KbQuizDetail extends KbQuiz {
  questions: KbQuizQuestion[];
}

export function listKbQuizzes(params: {
  targetType?: 'document' | 'chapter';
  targetId?: number;
} = {}): Promise<KbQuiz[]> {
  return apiClient
    .get<ApiResponse<KbQuiz[]>>('/kb/quizzes', { params })
    .then((response) => response.data.data);
}

export function getKbQuiz(quizId: number): Promise<KbQuizDetail> {
  return apiClient
    .get<ApiResponse<KbQuizDetail>>(`/kb/quizzes/${quizId}`)
    .then((response) => response.data.data);
}

export function createKbQuiz(payload: {
  targetType: 'document' | 'chapter';
  targetId: number;
  title: string;
  passThreshold?: number;
  status?: 'active' | 'archived' | 'draft';
}): Promise<KbQuiz> {
  return apiClient
    .post<ApiResponse<KbQuiz>>('/kb/quizzes', payload)
    .then((response) => response.data.data);
}

export function updateKbQuiz(
  quizId: number,
  payload: { title?: string; passThreshold?: number; status?: string },
): Promise<KbQuiz> {
  return apiClient
    .put<ApiResponse<KbQuiz>>(`/kb/quizzes/${quizId}`, payload)
    .then((response) => response.data.data);
}

export function deleteKbQuiz(quizId: number): Promise<void> {
  return apiClient.delete(`/kb/quizzes/${quizId}`).then(() => undefined);
}

export interface SaveKbQuizQuestionRequest {
  prompt: string;
  questionType?: 'single' | 'multi';
  choices: Array<{ label: string; isCorrect?: boolean }>;
}

export function addKbQuizQuestion(
  quizId: number,
  payload: SaveKbQuizQuestionRequest,
): Promise<{ kbQuizQuestionId: number }> {
  return apiClient
    .post<ApiResponse<{ kbQuizQuestionId: number }>>(`/kb/quizzes/${quizId}/questions`, payload)
    .then((response) => response.data.data);
}

export function updateKbQuizQuestion(
  questionId: number,
  payload: Partial<SaveKbQuizQuestionRequest>,
): Promise<void> {
  return apiClient.put(`/kb/questions/${questionId}`, payload).then(() => undefined);
}

export function deleteKbQuizQuestion(questionId: number): Promise<void> {
  return apiClient.delete(`/kb/questions/${questionId}`).then(() => undefined);
}

export interface KbQuizAttemptStarted {
  kbQuizAttemptId: number;
  startedAt: string | null;
}

export function startKbQuizAttempt(
  quizId: number,
  payload: { assignmentId?: number | null } = {},
): Promise<KbQuizAttemptStarted> {
  return apiClient
    .post<ApiResponse<KbQuizAttemptStarted>>(`/kb/quizzes/${quizId}/attempts`, payload)
    .then((response) => response.data.data);
}

export interface KbQuizAttemptResult {
  kbQuizAttemptId: number;
  scorePct: number;
  passed: boolean;
  passThreshold: number;
  totalQuestions: number;
  correctCount: number;
}

export function submitKbQuizAttempt(
  attemptId: number,
  answers: Array<{ questionId: number; selectedChoiceIds: number[] }>,
): Promise<KbQuizAttemptResult> {
  return apiClient
    .post<ApiResponse<KbQuizAttemptResult>>(`/kb/quiz-attempts/${attemptId}/submit`, { answers })
    .then((response) => response.data.data);
}

// ─── Announcements ───────────────────────────────────────────────────────────

export interface KbAnnouncement {
  kbAnnouncementId: number;
  accountId: number;
  kbDocumentId: number | null;
  title: string;
  body: string;
  audience: 'all' | 'group' | 'users' | string;
  audienceRef: Record<string, unknown>;
  requiresAck: boolean;
  publishedAt: string | null;
  createdBy: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  recipientCount?: number;
  seenCount?: number;
  ackCount?: number;
}

export interface KbMyAnnouncement {
  kbAnnouncementId: number;
  title: string;
  body: string;
  kbDocumentId: number | null;
  requiresAck: boolean;
  publishedAt: string | null;
  seenAt: string | null;
  acknowledgedAt: string | null;
}

export function listKbAnnouncements(params: { q?: string; page?: number; perPage?: number } = {}): Promise<{
  data: KbAnnouncement[];
  total: number;
  page: number;
  perPage: number;
}> {
  return apiClient
    .get<ApiResponse<KbAnnouncement[]>>('/kb/announcements', { params })
    .then((response) => ({
      data: response.data.data,
      total: Number(response.data.meta?.total ?? response.data.data.length),
      page: Number(response.data.meta?.page ?? 1),
      perPage: Number(response.data.meta?.perPage ?? response.data.data.length),
    }));
}

export function createKbAnnouncement(payload: {
  title: string;
  body?: string;
  kbDocumentId?: number | null;
  audience: 'all' | 'group' | 'users';
  audienceRef?: { userIds?: number[]; groupId?: number };
  requiresAck?: boolean;
  publishNow?: boolean;
}): Promise<KbAnnouncement> {
  return apiClient
    .post<ApiResponse<KbAnnouncement>>('/kb/announcements', payload)
    .then((response) => response.data.data);
}

export function deleteKbAnnouncement(announcementId: number): Promise<void> {
  return apiClient.delete(`/kb/announcements/${announcementId}`).then(() => undefined);
}

export function listMyKbAnnouncements(): Promise<KbMyAnnouncement[]> {
  return apiClient
    .get<ApiResponse<KbMyAnnouncement[]>>('/kb/my-announcements')
    .then((response) => response.data.data);
}

export function markKbAnnouncementSeen(announcementId: number): Promise<void> {
  return apiClient.post(`/kb/my-announcements/${announcementId}/seen`).then(() => undefined);
}

export function markAllKbAnnouncementsSeen(): Promise<{ updated: number }> {
  return apiClient
    .post<ApiResponse<{ updated: number }>>('/kb/my-announcements/seen-all')
    .then((response) => response.data.data);
}

export function acknowledgeKbAnnouncement(announcementId: number): Promise<void> {
  return apiClient.post(`/kb/my-announcements/${announcementId}/acknowledge`).then(() => undefined);
}

// ─── Query keys ──────────────────────────────────────────────────────────────
// Centralizing keys keeps invalidations consistent across the app.

export const kbQueryKeys = {
  all: ['kb'] as const,
  sources: () => ['kb', 'sources'] as const,
  documents: (params?: KnowledgeBaseDocumentListParams) => ['kb', 'documents', params ?? {}] as const,
  document: (id: number) => ['kb', 'documents', id] as const,
  categories: () => ['kb', 'categories'] as const,
  tags: () => ['kb', 'tags'] as const,
  syncEvents: (params?: KbSyncEventListParams) => ['kb', 'sync-events', params ?? {}] as const,
  syncEvent: (id: number) => ['kb', 'sync-events', id] as const,
  chapters: () => ['kb', 'chapters'] as const,
  chapter: (id: number) => ['kb', 'chapters', id] as const,
  assignments: (params?: KbAssignmentListParams) => ['kb', 'assignments', params ?? {}] as const,
  myAssignments: (include?: 'all') => ['kb', 'my-assignments', include ?? 'open'] as const,
  quizzes: () => ['kb', 'quizzes'] as const,
  quiz: (id: number) => ['kb', 'quizzes', id] as const,
  announcements: () => ['kb', 'announcements'] as const,
  myAnnouncements: () => ['kb', 'my-announcements'] as const,
};
